/**
 * MandatoryFilters Component
 *
 * Displays mandatory (required) filters as persistent inline dropdowns.
 * These filters are always visible in the widget's persistent controls bar.
 */

import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, Check } from 'lucide-react';
import type { QueryFilter, WidgetFilterConfig } from '../../types/analytics';
import { analyticsFilterService } from '../../services/analyticsService';

// Type for filter options response from API
interface FilterOptionsResponse {
  filter_key: string;
  filter_name: string;
  filter_type: 'static' | 'dynamic';
  description?: string;
  options: Array<{ value: string | number; label: string }>;
}

// Helper to get filter key from config (handles both snake_case and camelCase)
const getFilterKey = (config: WidgetFilterConfig): string =>
  (config as any).filterKey || config.filter_key || '';
const getFilterLabel = (config: WidgetFilterConfig): string =>
  (config as any).filter_label || (config as any).filterLabel || '';
const getSelectionType = (config: WidgetFilterConfig): string =>
  (config as any).selectionType || config.selection_type || 'single';

export interface MandatoryFiltersProps {
  /** Current filter values */
  filters: QueryFilter[];
  /** Mandatory filter configurations */
  filterConfigs: WidgetFilterConfig[];
  /** Callback when filters change */
  onFiltersChange: (filters: QueryFilter[]) => void;
  /** Whether the filters are disabled */
  disabled?: boolean;
  /** Additional className */
  className?: string;
}

/**
 * MandatoryFilters - Inline dropdowns for mandatory filters
 *
 * Renders each mandatory filter as a compact dropdown that's always visible.
 */
export const MandatoryFilters: React.FC<MandatoryFiltersProps> = ({
  filters,
  filterConfigs,
  onFiltersChange,
  disabled = false,
  className = '',
}) => {
  const [filterOptions, setFilterOptions] = useState<Record<string, FilterOptionsResponse>>({});
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOpenDropdown(null);
      }
    };

    if (openDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openDropdown]);

  // Fetch filter options
  useEffect(() => {
    const fetchOptions = async () => {
      if (!filterConfigs || filterConfigs.length === 0) {
        return;
      }

      setLoadingOptions(true);

      try {
        const filterKeys = filterConfigs.map((f) => getFilterKey(f)).filter(Boolean);
        const filterMap = await analyticsFilterService.batchGetFilterOptions(filterKeys, false);

        const options: Record<string, FilterOptionsResponse> = {};
        filterMap.forEach((value, key) => {
          options[key] = value;
        });

        setFilterOptions(options);
      } catch (err) {
        console.error('Failed to fetch mandatory filter options:', err);
        // Set empty fallback
        const fallbackOptions: Record<string, FilterOptionsResponse> = {};
        filterConfigs.forEach((config) => {
          const key = getFilterKey(config);
          fallbackOptions[key] = {
            filter_key: key,
            filter_name: key,
            filter_type: 'dynamic',
            options: [],
          };
        });
        setFilterOptions(fallbackOptions);
      } finally {
        setLoadingOptions(false);
      }
    };

    fetchOptions();
  }, [filterConfigs]);

  // Get current value for a filter
  const getFilterValue = (filterKey: string): string | string[] | null => {
    const filter = filters.find((f) => f.field === filterKey);
    if (!filter) return null;
    return filter.value as string | string[];
  };

  // Get display text for current filter value
  const getValueDisplayText = (filterKey: string): string => {
    const value = getFilterValue(filterKey);
    const options = filterOptions[filterKey]?.options || [];

    if (!value || (Array.isArray(value) && value.length === 0)) {
      return 'Select...';
    }

    if (Array.isArray(value)) {
      if (value.length === 1) {
        const option = options.find((o) => String(o.value) === String(value[0]));
        return option?.label || String(value[0]);
      }
      return `${value.length} selected`;
    }

    const option = options.find((o) => String(o.value) === String(value));
    return option?.label || String(value);
  };

  // Handle value selection
  const handleValueChange = (filterKey: string, selectedValue: string | number, isMulti: boolean) => {
    const currentFilter = filters.find((f) => f.field === filterKey);
    let newValue: string | string[] | number | number[];

    if (isMulti) {
      const currentValues = (currentFilter?.value as (string | number)[]) || [];
      if (currentValues.includes(selectedValue)) {
        newValue = currentValues.filter((v) => v !== selectedValue);
      } else {
        newValue = [...currentValues, selectedValue];
      }
    } else {
      newValue = selectedValue;
      setOpenDropdown(null); // Close dropdown for single select
    }

    // Update filters array
    const updatedFilters = currentFilter
      ? filters.map((f) =>
          f.field === filterKey
            ? { ...f, operator: (isMulti ? 'in' : '=') as any, value: newValue }
            : f
        )
      : [
          ...filters,
          {
            field: filterKey,
            operator: (isMulti ? 'in' : '=') as any,
            value: newValue,
          },
        ];

    onFiltersChange(updatedFilters);
  };

  // Check if value is selected
  const isValueSelected = (filterKey: string, value: string | number): boolean => {
    const filterValue = getFilterValue(filterKey);
    if (!filterValue) return false;
    if (Array.isArray(filterValue)) {
      return filterValue.includes(value as any);
    }
    return String(filterValue) === String(value);
  };

  if (!filterConfigs || filterConfigs.length === 0) {
    return null;
  }

  return (
    <div className={`mandatory-filters flex items-center gap-3 ${className}`} ref={dropdownRef}>
      {filterConfigs.map((config) => {
        const filterKey = getFilterKey(config);
        const filterData = filterOptions[filterKey];
        const filterLabel = getFilterLabel(config) || filterData?.filter_name || filterKey;
        const selectionType = getSelectionType(config);
        const isMulti = selectionType === 'multiselect';
        const options = filterData?.options || [];
        const isOpen = openDropdown === filterKey;

        return (
          <div key={filterKey} className="relative flex items-center gap-1.5">
            <span className="text-xs font-medium text-gray-500">{filterLabel}:</span>
            <button
              type="button"
              onClick={() => setOpenDropdown(isOpen ? null : filterKey)}
              disabled={disabled || loadingOptions}
              className={`
                flex items-center gap-1 px-2 py-1 h-7 text-xs
                bg-white border border-gray-200 rounded-md
                hover:bg-gray-50 transition-colors
                ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                ${isOpen ? 'ring-1 ring-blue-500 border-blue-500' : ''}
              `}
            >
              {loadingOptions ? (
                <span className="animate-spin h-3 w-3 border-2 border-gray-300 border-t-gray-600 rounded-full" />
              ) : (
                <>
                  <span className="max-w-[120px] truncate">{getValueDisplayText(filterKey)}</span>
                  <ChevronDown className={`h-3 w-3 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                </>
              )}
            </button>

            {/* Dropdown */}
            {isOpen && options.length > 0 && (
              <div className="absolute top-full left-0 mt-1 z-50 min-w-[160px] max-w-[240px] max-h-[200px] overflow-auto bg-white border border-gray-200 rounded-md shadow-lg">
                {options.map((option) => {
                  const isSelected = isValueSelected(filterKey, option.value);
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => handleValueChange(filterKey, option.value, isMulti)}
                      className={`
                        w-full flex items-center gap-2 px-3 py-2 text-xs text-left
                        hover:bg-gray-50 transition-colors
                        ${isSelected ? 'bg-blue-50 text-blue-700' : 'text-gray-700'}
                      `}
                    >
                      {isMulti && (
                        <div
                          className={`
                            w-4 h-4 border rounded flex items-center justify-center
                            ${isSelected ? 'bg-blue-500 border-blue-500' : 'border-gray-300'}
                          `}
                        >
                          {isSelected && <Check className="h-3 w-3 text-white" />}
                        </div>
                      )}
                      <span className="truncate">{option.label}</span>
                      {!isMulti && isSelected && (
                        <Check className="h-3 w-3 text-blue-500 ml-auto" />
                      )}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Empty state */}
            {isOpen && options.length === 0 && (
              <div className="absolute top-full left-0 mt-1 z-50 min-w-[120px] bg-white border border-gray-200 rounded-md shadow-lg p-3">
                <span className="text-xs text-gray-400">No options available</span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default MandatoryFilters;
